import { registerPlugin } from "@capacitor/core";

// VBS 数据格式定义
export interface VBSData {
  type: string;
  timestamp: number;
  data: BumpyMap.SusPreviewInfoData & BumpyMap.WheelTrajectoryData;
}

export interface VBSSubscribePlugin {
  /**
   * 订阅 VBS 数据
   * 启动订阅后，所有数据将通过 "vbsDataChange" 事件发送
   */
  subscribe(): Promise<void>;

  /**
   * 添加数据监听器
   * @param eventName 事件名称，固定为 "vbsDataChange"
   * @param listenerFunc 回调函数，接收 VBSData 数据
   */
  addListener(
    eventName: "vbsDataChange",
    listenerFunc: (data: VBSData) => void
  ): Promise<{ remove: () => void }>;

  /**
   * 移除所有监听器
   */
  removeAllListeners(): Promise<void>;
}

const VBSSubscribe = registerPlugin<VBSSubscribePlugin>("VBSSubscribe");

// 创建 VBSSubscribeClient 类来管理订阅，类似于 CarClient
class VBSSubscribeClientClass {
  private eventListener: { remove: () => Promise<void> | void } | null = null;
  private isSubscribed = false;

  /**
   * 订阅 VBS 数据变化通知
   * @param callback 回调函数，当收到 VBS 数据时调用
   */
  async subscribe(callback: (data: VBSData) => void): Promise<void> {
    // 移除已存在的监听器
    if (this.eventListener) {
      await this.eventListener.remove();
      this.eventListener = null;
    }

    // 如果还未订阅，先启动订阅
    if (!this.isSubscribed) {
      await VBSSubscribe.subscribe();
      this.isSubscribed = true;
    }

    // 添加新的监听器
    this.eventListener = await VBSSubscribe.addListener(
      "vbsDataChange",
      callback
    );
  }

  /**
   * 取消订阅 VBS 数据
   */
  async unsubscribe(): Promise<void> {
    // 移除事件监听器
    if (this.eventListener) {
      await this.eventListener.remove();
      this.eventListener = null;
    }
    this.isSubscribed = false;
  }

  /**
   * 移除所有监听器
   */
  async removeAllListeners(): Promise<void> {
    // 移除事件监听器
    if (this.eventListener) {
      await this.eventListener.remove();
      this.eventListener = null;
    }
    this.isSubscribed = false;

    // 调用原生方法移除所有监听器
    await VBSSubscribe.removeAllListeners();
  }
}

// 导出 VBSSubscribeClient 实例
export const VBSSubscribeClient = new VBSSubscribeClientClass();

// 默认导出 VBSSubscribeClient 实例
export default VBSSubscribeClient;
